import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Pin, Star, Inbox, ChevronLeft, Check, CheckCheck, X, Bell, ArrowDownUp, Megaphone, Users, Bot, ChevronDown, ChevronRight } from "lucide-react";
import { Avatar, Spinner, Empty } from "@/components/common";
import ChatWindow from "@/components/inbox/ChatWindow";
import FanInsights from "@/components/inbox/FanInsights";
import { LabelChips } from "@/components/inbox/FanLabels";
import { ChannelStrip } from "@/components/inbox/ChannelStrip";
import { FolderBar } from "@/components/inbox/FolderBar";
import { JoinTab } from "@/components/inbox/JoinRequests";
import { useAuth, useEvent, useEvents } from "@/context/AppContext";
import { api, toastErr } from "@/lib/api";
import { CONV_EVENTS } from "@/lib/inbox";
import { ago, money, isAdmin, fmtListTime, fmtFull, fmtMin } from "@/lib/format";
import { fanTitle, isOnline } from "@/lib/presence";
import { prefetchChat, prefetchMany, getList, putList } from "@/lib/chatCache";
import { useStaffTyping } from "@/lib/staffTyping";

const dot = (s) => (s === "connected" ? "bg-emerald-400" : s === "connecting" ? "bg-amber-400" : s === "demo" ? "bg-sky-400" : "bg-red-400");
const reconnecting = (a) => !a.is_demo && a.status !== "auth_required" && (a.status !== "connected" || a.live === false);
const pinOf = (c) => !!(c.pinned_here ?? c.telegram_pinned);
// TUTTE / ARCHIVIATE / Telegram folders: Telegram pins, then time. NON LETTE: bot-waiting chats first, then the rest ('Preferiti' and 'Alert' removed).
// ARCHIVIATE / other Telegram folders: Telegram pins, then time. Pin, favourite and alert are independent states.
const botWait = (c) => !!c.bot_pin_at;
const paidWait = (c) => !!c.paid_wait_at;
const groupOf = (c, mode) => (paidWait(c) ? "paidwait" : mode === "unread" ? (botWait(c) ? "botwait" : "rest") : pinOf(c) ? "pinned" : "rest");
const GROUPS = { paidwait: "⭐ Paganti in attesa", botwait: "🤖 In attesa dopo il bot", pinned: "Fissate su Telegram", rest: "Altre chat" };
// NON LETTE: chats answered by the bot and still waiting for a chatter always on top, longest wait first (whatever the sort)
const unreadOrder = (convs) => [...convs.filter(paidWait).sort((a, b) => new Date(a.paid_wait_at) - new Date(b.paid_wait_at)), ...convs.filter((c) => !paidWait(c) && botWait(c)).sort((a, b) => new Date(a.bot?.wait_from || a.bot_pin_at) - new Date(b.bot?.wait_from || b.bot_pin_at)), ...convs.filter((c) => !paidWait(c) && !botWait(c))];
const allOrder = (convs) => [...convs.filter(paidWait).sort((a, b) => new Date(a.paid_wait_at) - new Date(b.paid_wait_at)), ...convs.filter((c) => !paidWait(c))];
const byTime = (dir) => (a, b) => (paidWait(b) - paidWait(a)) || (pinOf(b) - pinOf(a)) || (dir === "asc" ? 1 : -1) * (new Date(a.last_message_at) - new Date(b.last_message_at));
const isCustom = (f) => f && !["all", "unread", "archived"].includes(f);
const handle = (a) => (a.username ? `@${String(a.username).replace(/^@/, "")}` : a.label);
const cleared = (c) => c.chat_cleared_at && (!c.last_message_at || new Date(c.chat_cleared_at) >= new Date(c.last_message_at));

function AccountBar({ accounts, selected, select }) {
  const { user } = useAuth();
  const key = `dopamine.hiddenAccounts.${user.id}`;
  const [hidden, setHidden] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  const save = (h) => { setHidden(h); localStorage.setItem(key, JSON.stringify(h)); };
  const visible = accounts.filter((a) => !hidden.includes(a.id));
  const off = accounts.filter((a) => hidden.includes(a.id));
  const [bots, setBots] = useState({});
  const loadBots = () => api.get("/bot/status").then((r) => setBots(r.data)).catch(() => {});
  useEffect(() => { loadBots(); const t = setInterval(loadBots, 60000); return () => clearInterval(t); }, []);
  useEvent("bot.updated", loadBots);
  return (
    <div className="shrink-0 flex items-center gap-2 overflow-x-auto thin-scroll border-b border-[#232738] bg-[#0D0E14] px-3 py-2" data-testid="account-bar">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 shrink-0 mr-1">Profilo</span>
      {visible.map((a) => {
        const on = selected === a.id;
        return (
          <div key={a.id} className={`shrink-0 flex items-center rounded-full border pl-1 pr-1 py-1 text-xs font-bold uppercase tracking-wide transition-colors ${on ? "border-violet-500 bg-violet-600/25 text-white" : "border-[#232738] text-slate-400 hover:text-white hover:border-violet-500/50"}`}>
            <button onClick={() => select(a.id)} data-testid={`account-chip-${a.username || a.id}`} aria-pressed={on} className="flex items-center gap-2 pr-1">
              <span className="relative"><Avatar name={a.username || a.label} url={a.avatar_url} size={22} /><span className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-[#0D0E14] ${dot(a.status)}`} /></span>
              <span data-testid={`account-chip-username-${a.id}`}>{handle(a)}</span>
              {reconnecting(a) && <span className="text-[9px] normal-case font-semibold text-amber-300" data-testid={`account-reconnecting-${a.id}`}>Connessione in corso…</span>}
              {a.status === "auth_required" && <span className="rounded bg-red-600/20 px-1 text-[9px] font-extrabold text-red-300" data-testid={`account-disconnected-${a.id}`}>SCOLLEGATO</span>}
              <span className={`num rounded-full px-1.5 text-[10px] ${a.unread > 0 ? "bg-red-600 text-white" : "bg-white/5 text-slate-500"}`} data-testid={`account-unread-${a.username || a.id}`}>{a.unread || 0}</span>
              {Math.min(a.paid_unread || 0, a.unread || 0) > 0 && <span title={`${Math.min(a.paid_unread, a.unread)} paganti non letti`} className="num rounded-full bg-amber-400 px-1.5 text-[10px] text-black" data-testid={`account-paid-${a.username || a.id}`}>{Math.min(a.paid_unread, a.unread)}</span>}
              {bots[a.id] && <span title={bots[a.id].reason} className={`flex items-center ${bots[a.id].active ? "text-emerald-400" : "text-slate-600"}`} data-testid={`account-bot-${a.username || a.id}`} data-active={bots[a.id].active ? "1" : "0"}><Bot className="h-3.5 w-3.5" />{bots[a.id].pinned > 0 && <span className="num ml-0.5 rounded-full bg-amber-400 px-1 text-[9px] font-bold text-black" data-testid={`account-bot-pinned-${a.username || a.id}`}>{bots[a.id].pinned}</span>}</span>}
              {on && <Check className="h-3 w-3 text-violet-300" />}
            </button>
            <button onClick={() => save([...hidden, a.id])} title="Nascondi profilo" className="p-0.5 rounded-full text-slate-500 hover:text-white hover:bg-white/10" data-testid={`account-hide-${a.username || a.id}`}><X className="h-3 w-3" /></button>
          </div>
        );
      })}
      {off.length > 0 && (
        <select value="" onChange={(e) => e.target.value && save(hidden.filter((x) => x !== e.target.value))} className="shrink-0 h-7 rounded-full border border-dashed border-[#334155] bg-transparent px-2 text-xs text-slate-400" data-testid="account-add-select">
          <option value="">+ Aggiungi profilo ({off.length})</option>{off.map((a) => <option key={a.id} value={a.id}>{handle(a)}</option>)}
        </select>
      )}
      {isAdmin(user) && <a href="/reports#reports-export" className="ml-auto shrink-0 rounded-full border border-[#232738] px-2.5 py-1 text-[11px] font-bold text-slate-300 hover:text-white hover:border-violet-500/50" data-testid="messages-export-btn" data-viewer-ok="1">⬇ Esporta</a>}
    </div>
  );
}

function PaidWait({ c }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 60000); return () => clearInterval(t); }, []);
  return <div className="mt-1 text-[10px] font-bold text-amber-300" data-testid={`conv-paid-wait-${c.id}`}>⏰ Pagante in attesa da {Math.max(1, Math.floor((Date.now() - new Date(c.paid_wait_at)) / 60000))} min</div>;
}

function ConvRow({ c, active, onClick, typing }) {
  const channel = c.kind === "channel";
  const group = c.kind === "group" || c.kind === "supergroup";
  const nonFan = channel || group;
  return (
    <button onClick={onClick} onMouseEnter={() => prefetchChat(c.id)} data-testid={`conversation-item-${c.id}`} draggable onDragStart={(e) => { e.dataTransfer.setData("text/dopamine-conv", c.id); e.dataTransfer.effectAllowed = "move"; }}
      style={{ contentVisibility: "auto", containIntrinsicSize: "auto 76px" }}
      className={`w-full flex gap-2.5 px-3 py-2.5 text-left border-b border-[#1A1D2B] transition-colors ${active ? "bg-violet-600/15" : paidWait(c) ? "bg-amber-400/15 hover:bg-amber-400/20 border-l-2 border-l-amber-300" : c.bot_pin_at ? "bg-amber-400/10 hover:bg-amber-400/15 border-l-2 border-l-amber-400" : "hover:bg-white/[0.03]"}`}>
      <div className="relative"><Avatar name={nonFan ? c.channel_title : c.fan_name || c.fan_username} size={36} />
        {!nonFan && isOnline(c.tg_status) && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0B0C10]" data-testid={`conv-online-${c.id}`} />}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          {paidWait(c) && <span className="text-[11px]" data-testid={`conv-paid-star-${c.id}`}>⭐</span>}
          {pinOf(c) && <Pin className="h-3 w-3 text-violet-400" data-testid={`conv-pinned-${c.id}`} />}
          {channel && <Megaphone className="h-3 w-3 text-sky-400" data-testid={`conv-channel-${c.id}`} />}
          {c.join_pending > 0 && <span className="rounded-full bg-violet-600 px-1.5 text-[9px] font-bold text-white" title="Richieste di accesso in attesa" data-testid={`conv-join-${c.id}`}>{c.join_pending}</span>}
          {group && <Users className="h-3 w-3 text-teal-400" data-testid={`conv-group-kind-${c.id}`} />}
          <span className={`text-sm truncate ${c.unread_count ? "font-bold text-white" : "font-semibold text-slate-200"}`} data-testid={`conv-title-${c.id}`}>{fanTitle(c)}</span>
          <span className="ml-auto text-[10px] text-slate-500 shrink-0" title={fmtFull(c.last_message_at)} data-testid={`conv-time-${c.id}`}>{fmtListTime(c.last_message_at)}</span>
        </div>
        <div className={`text-xs truncate ${c.unread_count ? "text-slate-200" : "text-slate-500"}`}>{cleared(c) ? <span className="italic text-slate-500" data-testid={`conv-cleared-${c.id}`}>Chat eliminata dal cliente</span> : <>{c.last_direction === "out" && !nonFan && <>{c.last_out_tg_id && c.read_outbox_max_id >= c.last_out_tg_id
          ? <CheckCheck className="inline h-3 w-3 mr-0.5 text-sky-300" data-testid={`conv-read-${c.id}`} /> : <Check className="inline h-3 w-3 mr-0.5 text-slate-500" data-testid={`conv-sent-${c.id}`} />}Tu: </>}{c.last_message_text}</>}</div>
        {paidWait(c) && <PaidWait c={c} />}
        {typing && <div className="mt-0.5 text-[10px] font-semibold text-yellow-300 truncate" data-testid={`conv-staff-typing-${c.id}`}>✍ {typing.name}</div>}
        {c.labels?.length > 0 && <div className="mt-1"><LabelChips labels={c.labels} testid={`conv-labels-${c.id}`} small /></div>}
        {c.bot?.count > 0 && <div className="mt-1 text-[10px] font-semibold text-amber-300" data-testid={`conv-bot-${c.id}`}>🤖 Bot ha risposto {c.bot.count} {c.bot.count === 1 ? "volta" : "volte"} · ultima {new Date(c.bot.last_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}{c.bot.wait_from && <> · attende da {fmtMin((Date.now() - new Date(c.bot.wait_from)) / 60000)}</>}</div>}
        {(c.bot_excluded || c.bot_never) && <span title={c.bot_excluded || "escluso: Mai bot"} className="mt-1 inline-block rounded bg-red-600/15 px-1 text-[9px] font-bold text-red-300" data-testid={`conv-bot-never-${c.id}`}>🚫 Bot</span>}
        <div className="flex items-center gap-1.5 mt-1 text-[10px]">
          {nonFan ? <span className={`rounded px-1.5 py-0.5 font-bold ${group ? "bg-teal-500/15 text-teal-300" : "bg-sky-500/15 text-sky-300"}`}>{group ? "GRUPPO" : "CANALE"}</span>
            : <span className="num rounded bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5" data-testid="conv-total-spent">{money(c.total_spent)}</span>}
          <span className="text-slate-500 truncate">{(nonFan || !(c.fan_username && c.fan_name)) ? (c.account_username ? `@${c.account_username}` : "") : `@${c.fan_username}`}</span>
          <span className="text-slate-600 truncate ml-auto">{c.chatter_name}</span>
          {c.unread_count > 0 && <span className="num rounded-full bg-violet-600 text-white px-1.5" data-testid="conv-unread">{c.unread_count}</span>}
        </div>
      </div>
    </button>
  );
}

function SearchResults({ convs, ...rest }) {
  const main = convs.filter((c) => !c.archived);
  const arch = convs.filter((c) => c.archived);
  return (
    <div data-testid="search-results">
      {main.length > 0 && <ConvList convs={main} {...rest} />}
      {arch.length > 0 && (<>
        <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide text-slate-500" data-testid="search-archived-header">Chat archiviate · {arch.length}</div>
        <ConvList convs={arch} {...rest} />
      </>)}
    </div>
  );
}


function ConvList({ convs: all, activeId, open, mode, typing = {} }) {
  const convs = mode === "unread" ? unreadOrder(all) : allOrder(all);
  return convs.map((c, i) => (
    <div key={c.id}>
      {(i === 0 || groupOf(convs[i - 1], mode) !== groupOf(c, mode)) && <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid={`conv-group-${groupOf(c, mode)}`}>{GROUPS[groupOf(c, mode)]}{groupOf(c, mode) === "botwait" && ` (${convs.filter(botWait).length})`}</div>}
      <ConvRow c={c} active={c.id === activeId} onClick={() => open(c.id)} typing={typing[c.id]} />
    </div>
  ));
}

// Bot chats (read-only mirror, never answered): separate closed section, counted in the folders like Telegram.
function BotSection({ accountId, folder, sort, info, activeId, open }) {
  const [show, setShow] = useState(false);
  const [items, setItems] = useState(null);
  useEffect(() => { setShow(false); setItems(null); }, [accountId, folder]);
  const toggle = async () => {
    const next = !show;
    setShow(next);
    if (!next) return;
    try { const { data } = await api.get("/conversations", { params: { account_id: accountId, folder, sort, inbox: true, kind: "bot", limit: 100 }, timeout: 9000 }); setItems(data.items); } catch (e) { toastErr(e); setItems([]); }
  };
  if (!info?.total) return null;
  return (
    <div className="border-t border-[#232738]" data-testid="bot-section">
      <button onClick={toggle} className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors" data-testid="bot-section-toggle">
        <span className="flex items-center gap-1.5">{show ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}<Bot className="h-3.5 w-3.5" />Bot · {info.total}</span>
        {info.unread > 0 && <span className="rounded-full bg-slate-600/40 px-1.5 py-0.5 text-slate-200 normal-case" data-testid="bot-section-unread">{info.unread} non lette</span>}
      </button>
      {show && (!items ? <Spinner /> : items.map((c) => <ConvRow key={c.id} c={c} active={c.id === activeId} onClick={() => open(c.id)} />))}
    </div>
  );
}

// Folders of the selected account only; stale responses from a previous account are dropped.
function useAccountFolders(accountId) {
  const [folders, setFolders] = useState([]);
  const ref = useRef(accountId);
  ref.current = accountId;
  const load = useCallback(() => {
    const aid = accountId;
    if (!aid) return;
    api.get(`/telegram/accounts/${aid}/folders`, { timeout: 9000 }).then((r) => { if (ref.current === aid) setFolders(r.data); }).catch(() => {});
  }, [accountId]);
  useEffect(() => { setFolders([]); load(); }, [load]);
  const onEvt = (d) => (d.account_id || d.conversation?.account_id) === ref.current && load();
  useEvent("TELEGRAM_FOLDERS_UPDATED", onEvt);
  useEvent("UNREAD_COUNTERS", (d) => d.account_id === ref.current && setFolders((p) => p.map((f) => ({ ...f, unread: d.folders?.[String(f.id)] ?? f.unread, ...("paid_unread" in f ? { paid_unread: Math.min(d.paid || 0, d.unread || 0) } : {}) }))));
  useEvent("TELEGRAM_FOLDER_VISIBILITY_UPDATED", onEvt);
  useEvents(CONV_EVENTS, onEvt);
  useEvent("message.new", onEvt);
  return [folders, load];
}

function useConversations(accountId, folder, q, sort, paidFirst) {
  const [convs, setConvs] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [bots, setBots] = useState(null);
  const ctx = useRef("");
  const key = `${accountId}|${folder}|${q}|${sort}${paidFirst ? "|p" : ""}`;
  ctx.current = key;
  const busy = useRef(false);
  const load = useCallback(async (skip = 0) => {
    if (!accountId) { setConvs([]); setHasMore(false); return; }
    if (skip && busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/conversations", { params: { account_id: accountId, folder, q: q || undefined, sort, inbox: true, skip, limit: 50, paid_first: paidFirst || undefined }, timeout: 9000 });
      if (ctx.current !== key) return;
      const items = data.items.filter((c) => c.account_id === accountId);
      setConvs((prev) => { const next = skip ? [...(prev || []), ...items.filter((x) => !(prev || []).some((y) => y.id === x.id))] : items; if (!q) putList(key, next.slice(0, 80)); return next; });
      setHasMore(data.has_more);
      if (!skip) setBots(data.bots || null);
    } catch (e) { if (ctx.current === key) { toastErr(e); setConvs((p) => p || []); } } finally { busy.current = false; }
  }, [key]); // eslint-disable-line
  // Instant profile/folder switch: last known list shown at once, refreshed in background.
  useEffect(() => { setConvs(getList(key) || null); const t = setTimeout(() => load(0), q ? 250 : 0); return () => clearTimeout(t); }, [load, q]); // eslint-disable-line
  return { convs, setConvs, hasMore, load, bots };
}

export default function Messages() {
  const { user } = useAuth();
  const sortKey = `dopamine.inbox.sort.${user.id}`;
  const accKey = `dopamine.inbox.account.${user.id}`;
  const [sort, setSort] = useState(() => (localStorage.getItem(sortKey) === "asc" ? "asc" : "desc"));
  const [sp, setSp] = useSearchParams();
  // The profile bar is shown AT ONCE (first render) from the last list saved in this browser, then refreshed by the server: never empty
  const ACC_CACHE = `dopamine.accbar.${user.id}`;
  const [cached] = useState(() => { try { const c = JSON.parse(localStorage.getItem(ACC_CACHE) || "null"); return Array.isArray(c) ? c : []; } catch { return []; } });
  const [accounts, setAccounts] = useState(cached);
  const [accountId, setAccountId] = useState(() => sp.get("a") || localStorage.getItem(accKey) || cached[0]?.id || null);
  const folderKey = (aid) => `dopamine.inbox.folder.${user.id}.${aid}`;
  const [folder, setFolder] = useState(() => (sp.get("f") === "unread" ? "unread" : null) || (accountId && localStorage.getItem(folderKey(accountId))) || "all");
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(sp.get("c") || sp.get("conv"));
  const [mobileView, setMobileView] = useState(sp.get("c") || sp.get("conv") ? "chat" : "list");
  const [syncCount, setSyncCount] = useState(null);
  const [folders, loadFolders] = useAccountFolders(accountId);
  // dashboard 'paganti' card: paying clients on top in Non lette, until the user picks another tab or profile
  const [paidFirst, setPaidFirst] = useState(sp.get("p") === "1");
  const { convs, setConvs, hasMore, load: loadConvs, bots } = useConversations(accountId, folder, q, sort, paidFirst);
  const typing = useStaffTyping();

  const accRef = useRef(accountId);
  accRef.current = accountId;
  const activeRef = useRef(null);
  activeRef.current = activeId;
  const listed = useRef([]);
  const loadAccounts = useCallback(() => api.get("/telegram/accounts", { params: { inbox: true }, timeout: 9000 }).then((r) => {
    setAccounts(r.data);
    try { localStorage.setItem(ACC_CACHE, JSON.stringify(r.data)); } catch { /* quota */ }
    const cur = accRef.current;
    // A chat opened by link keeps its own profile (set by followChat) until the user picks another one.
    const chatDriven = activeRef.current && cur && !listed.current.includes(cur);
    if (!r.data.some((a) => a.id === cur) && !chatDriven) {
      // Profile no longer available: never leave a chat of it open under another profile.
      if (cur) { setActiveId(null); setMobileView("list"); setSp({}); }
      setAccountId(r.data[0]?.id || null);
    }
    listed.current = r.data.map((a) => a.id);
  }).catch(() => { setTimeout(() => loadAccounts(), 3000); }), []); // eslint-disable-line
  useEffect(() => {
    loadAccounts(); localStorage.removeItem("dopamine.inbox.accounts");
  }, [loadAccounts]);
  // Bursts of status / list events (e.g. 9 profiles reconnecting) = ONE reload of the bar.
  const acTimer = useRef(null);
  const soonAccounts = useCallback(() => { clearTimeout(acTimer.current); acTimer.current = setTimeout(loadAccounts, 600); }, [loadAccounts]);
  useEffect(() => () => clearTimeout(acTimer.current), []);
  useEffect(() => { if (accountId) localStorage.setItem(accKey, accountId); }, [accountId]); // eslint-disable-line
  useEffect(() => { const c = sp.get("c") || sp.get("conv"); if (c) { setActiveId(c); setMobileView("chat"); } }, [sp]);
  // The folder changes ONLY on the user's click: a folder list refresh never moves him (a missing one falls back to Tutte once).
  useEffect(() => { if (folders.length && !folders.some((f) => f.id === folder && (f.visible || isAdmin(user)))) setFolder("all"); }, [folders.length ? folders.map((f) => f.id).join() : ""]); // eslint-disable-line
  // Dashboard 'Chat non lette per profilo' link (?f=unread): applied ONCE on arrival, then removed from the address
  const linkUnread = useRef(sp.get("f") === "unread");
  useEffect(() => {
    if (!linkUnread.current || !folders.length) return;
    linkUnread.current = false;
    const u = folders.find((f) => f.unread_rule) || folders.find((f) => f.id === "unread");
    if (u) { setFolder(u.id); localStorage.setItem(folderKey(accountId), u.id); }
    if (sp.get("f")) { const n = new URLSearchParams(sp); n.delete("f"); n.delete("p"); setSp(n, { replace: true }); }
  }, [folders]); // eslint-disable-line

  const selectAccount = (aid) => {
    if (aid === accountId) return;
    setPaidFirst(false);
    setActiveId(null);
    setSp({});
    setMobileView("list");
    setSyncCount(null);
    setFolder(localStorage.getItem(folderKey(aid)) || "all");
    setAccountId(aid);
  };
  // Chat opened from a link/notification of another profile: the selected profile follows the chat (same account everywhere).
  const followChat = (aid) => {
    if (aid === accRef.current) return;
    setSyncCount(null);
    setFolder(localStorage.getItem(folderKey(aid)) || "all");
    setAccountId(aid);
  };
  const selectFolder = (f) => { setPaidFirst(false); setFolder(f); localStorage.setItem(folderKey(accountId), f); };
  const upsert = (conv) => {
    if (!conv || conv.account_id !== accountId) return;
    if (conv.kind === "channel" && !q) return;  // channels are in the cards row above the list
    const unreadView = folder === "unread" || folders.find((f) => f.id === folder)?.unread_rule;
    // NON LETTE: answered (by us, site or Telegram) or read elsewhere = out of the list at once
    if (unreadView && !q && !conv.bot_pin_at && (conv.last_direction === "out" || (!conv.unread_count && conv.id !== activeRef.current))) {
      if (kept.current?.id === conv.id) kept.current = null;
      setConvs((prev) => prev?.filter((c) => c.id !== conv.id));
      return;
    }
    if (folder !== "all" || q) {
      if (conv.id === activeRef.current && kept.current) kept.current.conv = { ...kept.current.conv, ...conv };
      // in place when already listed (no reload, list and scroll stay where they are); new chats arrive with one reload
      setConvs((prev) => (prev?.some((c) => c.id === conv.id) ? prev.map((c) => (c.id === conv.id ? { ...c, ...conv, labels: conv.labels || c.labels } : c)) : prev));
      if (!(convs || []).some((c) => c.id === conv.id)) loadConvs(0);
      return;
    }
    setConvs((prev) => {
      if (!prev) return prev;
      const rest = prev.filter((c) => c.id !== conv.id);
      return conv.archived || conv.inactive ? rest : [conv, ...rest].sort(byTime(sort));
    });
  };
  useEvent("message.new", (d) => upsert(d.conversation));
  useEvents(CONV_EVENTS, (d) => upsert(d.conversation));
  // Counters arrive already computed by the server (true totals, not the loaded page) and identical for every connected user.
  useEvent("UNREAD_COUNTERS", (d) => setAccounts((p) => p.map((a) => (a.id === d.account_id ? { ...a, unread: d.unread, paid_unread: d.paid, unread_conversations: d.unread_conversations } : a))));
  useEvent("TELEGRAM_FOLDERS_UPDATED", (d) => d.account_id === accountId && folder !== "all" && loadConvs(0));
  useEvent("account.status", (d) => { setAccounts((p) => p.map((a) => (a.id === d.account_id ? { ...a, status: d.status } : a))); soonAccounts(); });
  useEvent("account.synced", (d) => { if (d.account_id === accountId) { setSyncCount(null); loadConvs(0); } });
  const lastProg = useRef(0);
  useEvent("account.sync_progress", (d) => {
    if (d.account_id !== accountId) return;
    setSyncCount({ count: d.count, total: d.total });
    if (Date.now() - lastProg.current > 4000) { lastProg.current = Date.now(); loadConvs(0); }
  });
  useEvent("supervision.updated", () => loadConvs(0));
  useEvent("PRESENCE_UPDATED", (d) => setConvs((p) => p?.map((c) => (c.id === d.conversation?.id ? { ...c, ...d.conversation, labels: c.labels } : c))));
  // Preload the first chats of the list: opening them is instant.
  const preKey = useRef("");
  useEffect(() => {
    const ids = (convs || []).slice(0, 12).map((c) => c.id);
    const k = `${accountId}|${folder}|${ids[0] || ""}`;
    if (ids.length && preKey.current !== k) { preKey.current = k; prefetchMany(ids); }
  }, [convs, accountId, folder]);
  useEvent("MESSAGES_ACCOUNT_LIST_UPDATED", soonAccounts);
  useEvent("rt.resync", () => { loadConvs(0); loadFolders(); });
  useEvent("CLIENT_LABEL_UPDATED", (d) => setConvs((p) => p?.map((c) => (c.fan_id === d.fan_id ? { ...c, labels: d.labels } : c))));
  useEvent("LABELS_UPDATED", () => loadConvs(0));

  // Opening a chat = read: counters drop at once (server confirms); in 'Non lette' the open chat stays in place until another one is opened.
  const kept = useRef(null);
  const open = (id) => {
    const i = (convs || []).findIndex((c) => c.id === id);
    const c = convs?.[i];
    kept.current = c ? { id, index: i, conv: { ...c, unread_count: 0, bot_pin_at: user.role === "viewer" ? c.bot_pin_at : null } } : null;
    if (c?.bot_pin_at && user.role !== "viewer") setConvs((p) => p?.map((x) => (x.id === id ? { ...x, bot_pin_at: null } : x)));  // opened by staff: out of the bot group at once
    if (c?.unread_count) {
      setConvs((p) => p?.map((x) => (x.id === id ? { ...x, unread_count: 0 } : x)));
      setAccounts((p) => p.map((a) => (a.id === c.account_id ? { ...a, unread: Math.max(0, (a.unread || 0) - 1) } : a)));
    }
    setActiveId(id); setMobileView("chat"); setSp({ c: id });
  };
  const k = kept.current;
  const shown = convs && k && k.id === activeId && !convs.some((c) => c.id === k.id) && !q
    ? [...convs.slice(0, k.index), k.conv, ...convs.slice(k.index)] : convs;
  const active = shown?.find((c) => c.id === activeId);
  const isChannel = ["channel", "group", "supergroup"].includes(active?.kind);
  const mode = folder === "unread" || folders.find((f) => f.id === folder)?.unread_rule ? "unread" : folder === "all" ? "all" : "pin";
  // Site -> Telegram: moves the chat in the REAL Telegram folders (updateDialogFilter); the mirror refreshes from Telegram.
  const moveConv = async (convId, to, from = isCustom(folder) ? folder : null) => {
    const title = (id) => folders.find((f) => f.id === id)?.title;
    try {
      await api.post(`/telegram/accounts/${accountId}/folders/move`, { conversation_id: convId, to_folder_id: to, from_folder_id: from === to ? null : from });
      toast.success(to ? `Chat spostata in "${title(to)}" anche su Telegram` : `Chat rimossa da "${title(from)}" anche su Telegram`);
      loadFolders(); loadConvs(0);
    } catch (e) { toastErr(e); }
  };

  const folderBar = () => accountId && <FolderBar wrap testid="folder-bar" accountId={accountId} folders={folders} folder={folder} onSelect={selectFolder} admin={isAdmin(user)} onChanged={loadFolders} onDropConv={(cid, fid) => moveConv(cid, fid)}
    extra={folders[0]?.requests_hidden ? null : <JoinTab accountId={accountId} auto />} />;
  const onListScroll = (e) => { const el = e.currentTarget; if (hasMore && convs && el.scrollTop + el.clientHeight > el.scrollHeight - 300) loadConvs(convs.length); };

  return (
    <div className="h-full flex flex-col" data-testid="messages-page">
      <AccountBar accounts={accounts} selected={accountId} select={selectAccount} />
      <div className="flex-1 min-h-0 flex">
        <div className={`${mobileView === "list" ? "flex" : "hidden"} md:flex w-full md:w-72 xl:w-[340px] shrink-0 flex-col border-r border-[#232738]`} data-testid="conversation-list">
          <div className="p-2.5 border-b border-[#232738] space-y-2">
            {syncCount != null && (user.role === "chatter"
              ? <div className="flex items-center gap-1 text-[10px] text-slate-500" data-testid="dialog-sync-progress"><span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />Sincronizzazione</div>
              : <div className="text-[10px] text-amber-300" data-testid="dialog-sync-progress">Sincronizzazione chat {syncCount.count}{syncCount.total ? ` / ${syncCount.total}` : ""}</div>)}
            <div className="flex items-center gap-1.5">
              <ArrowDownUp className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <select value={sort} onChange={(e) => { setSort(e.target.value); localStorage.setItem(sortKey, e.target.value); }} className="flex-1 h-8 rounded-md border border-[#232738] bg-[#12141D] px-2 text-xs text-slate-300 outline-none focus:border-violet-500" data-testid="inbox-sort-select">
                <option value="desc">Più recenti → meno recenti</option>
                <option value="asc">Meno recenti → più recenti</option>
              </select>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca conversazioni…" data-testid="conversation-search-input"
                className="w-full rounded-md border border-[#232738] bg-[#12141D] py-2 pl-8 pr-3 text-sm outline-none focus:border-violet-500" />
            </div>
          </div>
          {folderBar()}
          {accountId && !q && <ChannelStrip accountIds={[accountId]} accounts={accounts} activeId={activeId} open={open} />}
          <div className="flex-1 overflow-y-auto thin-scroll" onScroll={onListScroll} data-testid="conversation-scroll">
            {!accountId ? <Empty title="Nessun profilo disponibile" text="Non hai account Telegram autorizzati." icon={Inbox} />
              : !shown ? <Spinner /> : !shown.length ? <Empty title="Nessuna conversazione" icon={Inbox} />
              : q ? <SearchResults convs={shown} activeId={activeId} open={open} mode={mode} />
              : <ConvList convs={shown} activeId={activeId} open={open} mode={mode} typing={typing} />}
            {hasMore && <button onClick={() => loadConvs(convs.length)} className="w-full py-3 text-xs text-violet-400" data-testid="load-more-conversations">Carica altre</button>}
            {accountId && !q && <BotSection accountId={accountId} folder={folder} sort={sort} info={bots} activeId={activeId} open={open} />}
          </div>
        </div>
        <div className={`${mobileView === "list" ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col`} data-testid="right-pane">
          <div className="flex-1 min-h-0 flex">
        <div className={`${mobileView === "chat" ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col`}>
          {activeId ? (<>
            {sp.get("from") === "bot" && <a href="/bot-history" className="absolute right-3 top-14 z-20 rounded-full bg-violet-600 px-3 py-1 text-xs font-bold text-white shadow" data-testid="back-to-bot-history">← Torna allo storico</a>}
            <ChatWindow focusMsg={sp.get("m")} key={activeId} convId={activeId} accountId={accountId} onConvAccount={followChat} initial={active} onBack={() => { setMobileView("list"); setActiveId(null); setSp({}); }} onUpdated={upsert}
              folders={folders.filter((f) => !f.builtin)} folder={folder} onMove={moveConv}
              onToggleInsights={() => setMobileView("insights")} />
          </>) : <div className="flex-1 flex items-center justify-center"><Empty title="Seleziona una conversazione" icon={Inbox} /></div>}
        </div>
        {activeId && !isChannel && (
          <div className={`${mobileView === "insights" ? "flex w-full" : "hidden lg:flex w-64 xl:w-[290px]"} shrink-0 flex-col border-l border-[#232738] bg-[#0D0E14]`} data-testid="insights-pane">
            {mobileView === "insights" && <button onClick={() => setMobileView("chat")} className="flex items-center gap-1 p-3 text-sm text-violet-400 lg:hidden" data-testid="insights-back-btn"><ChevronLeft className="h-4 w-4" />Torna alla chat</button>}
            <FanInsights key={active?.fan_id || activeId} convId={activeId} fanId={active?.fan_id} />
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
